import {
  fetchBaseQuery,
  type BaseQueryFn,
  type FetchArgs,
  type FetchBaseQueryError,
  type FetchBaseQueryMeta,
} from "@reduxjs/toolkit/query/react";
import type { RootState } from "@/store/types";
import { forceLogout } from "@/store/slices/auth";
import { API_URLS, API_SETTINGS, API_ERRORS, type ApiUrlKey } from "./config";
import {
  LogoutService,
  performTokenRefresh,
  resetAuthFailureCounter,
  getRefreshState,
  type AuthTokenMode,
} from "./services";

const TIMEZONE_HEADER = "X-Timezone";
const AI_API_KEY_HEADER = "X-API-Key";

export interface ExtraOptions {
  skipAuthRetry?: boolean;
}

interface BaseQueryOptions {
  authMode?: AuthTokenMode;
  forwardProjectBearerForAi?: boolean;
  includeTimezoneHeader?: boolean;
  preserveTransportErrors?: boolean;
  sensitive?: boolean;
  isContextCurrent?: (initial: RootState, current: RootState) => boolean;
  getContextVersion?: () => unknown;
}
type ApiErrorStatus =
  | number
  | "FETCH_ERROR"
  | "TIMEOUT_ERROR"
  | "PARSING_ERROR"
  | "CUSTOM_ERROR";

// Error message mapping - defined once outside function
const ERROR_MAP: Readonly<Record<ApiErrorStatus, string>> = {
  400: "Bad request. Please check your input.",
  401: API_ERRORS.UNAUTHORIZED,
  403: API_ERRORS.FORBIDDEN,
  404: API_ERRORS.NOT_FOUND,
  429: API_ERRORS.RATE_LIMIT,
  500: API_ERRORS.SERVER,
  502: API_ERRORS.SERVER,
  503: API_ERRORS.SERVER,
  FETCH_ERROR: API_ERRORS.NETWORK,
  TIMEOUT_ERROR: API_ERRORS.TIMEOUT,
  PARSING_ERROR: "Failed to parse response.",
  CUSTOM_ERROR: API_ERRORS.UNKNOWN,
};

const normalizeHeaderTimezone = (timezone: unknown): string | null => {
  if (typeof timezone !== "string") return null;

  const trimmedTimezone = timezone.trim();
  return trimmedTimezone ? trimmedTimezone : null;
};

const getAccountTimezone = (state: RootState): string | null => {
  const profile = state.businessProfile?.currentProfile;

  return normalizeHeaderTimezone(
    profile?.accountDetails?.timezone ?? profile?.accountDefaults?.timezone,
  );
};

const hasFormDataBody = (arg: unknown): boolean => {
  if (!arg || typeof arg === "string") return false;
  if (typeof FormData === "undefined") return false;
  const body = (arg as Partial<FetchArgs>).body;
  return body instanceof FormData;
};

// Create base query with auth and error handling
export const createBaseQuery = (
  baseUrl: ApiUrlKey = "main",
  options: BaseQueryOptions = {},
): BaseQueryFn<
  string | FetchArgs,
  unknown,
  FetchBaseQueryError | { status: number | string; data: unknown },
  ExtraOptions,
  FetchBaseQueryMeta
> => {
  const authMode = options.authMode ?? "auto";
  const forwardProjectBearerForAi = options.forwardProjectBearerForAi ?? false;
  const isAiBaseQuery = baseUrl === "ai";
  const isPublicBaseQuery = baseUrl === "public";
  const baseQuery = fetchBaseQuery({
    baseUrl: API_URLS[baseUrl],
    timeout: isAiBaseQuery ? API_SETTINGS.aiTimeout : API_SETTINGS.timeout,
    prepareHeaders: (headers, { getState, arg }) => {
      const isMultipartRequest = hasFormDataBody(arg);
      if (!headers.has("Accept")) {
        headers.set(
          "Accept",
          isMultipartRequest ? "*/*" : "application/json, text/plain, */*",
        );
      }
      const shouldSkipContentType =
        isMultipartRequest ||
        headers.has("Content-Type") ||
        headers.has("X-Skip-Content-Type");
      if (!shouldSkipContentType) {
        headers.set("Content-Type", "application/json");
      }
      headers.delete("X-Skip-Content-Type");
      const state = getState() as RootState;
      const accountTimezone = getAccountTimezone(state);
      if (accountTimezone && options.includeTimezoneHeader !== false) {
        headers.set(TIMEZONE_HEADER, accountTimezone);
      } else {
        headers.delete(TIMEZONE_HEADER);
      }

      const authState = state.auth;

      if (isAiBaseQuery || isPublicBaseQuery) {
        headers.delete("Authorization");
        if (isAiBaseQuery && API_SETTINGS.aiApiKey) {
          headers.set(AI_API_KEY_HEADER, API_SETTINGS.aiApiKey);
        } else {
          headers.delete(AI_API_KEY_HEADER);
        }
        if (isAiBaseQuery && forwardProjectBearerForAi) {
          const token = authState?.projectAccessToken ?? authState?.accessToken;
          if (token) {
            headers.set("Authorization", `Bearer ${token}`);
          }
        }
        return headers;
      }

      const token =
        authMode === "identity"
          ? authState?.accessToken
          : authMode === "project"
            ? authState?.projectAccessToken
            : authState?.projectAccessToken ?? authState?.accessToken;
      if (token) {
        headers.set("Authorization", `Bearer ${token}`);
        //  headers.set('x-cin', );
        if (API_SETTINGS.debugMode && !options.sensitive) {
          console.log(
            "[API] Using token (first 20 chars):",
            token.substring(0, 20) + "...",
          );
        }
      }
      return headers;
    },
  });

  return async (args, api, extraOptions = {}) => {
    const initialState = api.getState() as RootState;
    const initialContextVersion = options.getContextVersion?.();
    const contextChanged = () => options.getContextVersion?.() !== initialContextVersion ||
      (options.isContextCurrent && !options.isContextCurrent(initialState, api.getState() as RootState));
    const staleContext = () => ({ error: { status: 403, data: { message: "Session or workspace changed. Retry from the current workspace." } } });
    let result = await baseQuery(args, api, extraOptions);
    if (contextChanged()) return staleContext();
    // Handle 401 Unauthorized - attempt token refresh
    if (
      result.error?.status === 401 &&
      !extraOptions.skipAuthRetry &&
      !isAiBaseQuery &&
      !isPublicBaseQuery
    ) {
      const state = api.getState() as RootState;
      const tokenForRetry =
        authMode === "identity"
          ? state.auth?.accessToken
          : authMode === "project"
            ? state.auth?.projectAccessToken
            : state.auth?.projectAccessToken ?? state.auth?.accessToken;

      if (tokenForRetry) {
        console.log("[API] Got 401, attempting token refresh...");
        const refreshResult = await performTokenRefresh({
          dispatch: api.dispatch,
          getState: () => api.getState() as RootState,
        }, authMode);
        if (contextChanged()) return staleContext();
        if (refreshResult.success) {
          // Retry the original request with new token
          console.log("[API] Token refreshed, retrying original request");
          result = await baseQuery(args, api, extraOptions);
        } else {
          // Token refresh failed - perform comprehensive cleanup and force logout
          console.error(
            "[API] Token refresh failed - performing comprehensive cleanup and forcing logout",
          );
          const rState = getRefreshState();
          LogoutService.setInProgress(true);
          try {
            await LogoutService.performFullCleanup({
              dispatch: api.dispatch,
              forceLogoutAction: forceLogout,
            });
          } catch (err) {
            console.error("[API] Cleanup error during force logout:", err);
          }
          // Reset the refresh state
          rState.consecutiveFailures = 0;
          rState.isRefreshing = false;
          LogoutService.setInProgress(false);
          if (options.preserveTransportErrors) return result;
          return {
            error: { status: 401, data: { message: API_ERRORS.UNAUTHORIZED } },
          };
        }
      }
    }

    // Handle other errors - map to user-friendly messages
    if (result.error) {
      if (options.preserveTransportErrors) return result;
      const { status } = result.error;
      const errorData = result.error.data as
        | Record<string, unknown>
        | undefined;
      const apiMessage =
        typeof errorData?.message === "string" ? errorData.message : undefined;
      if (API_SETTINGS.debugMode) {
        console.error("[API Error]", {
          status,
          errorData,
          endpoint: typeof args === "string" ? args : args.url,
        });
      }
      return {
        error: {
          status,
          data: {
            ...errorData,
            message:
              apiMessage ||
              ERROR_MAP[status as ApiErrorStatus] ||
              API_ERRORS.UNKNOWN,
          },
        },
      };
    }
    return result;
  };
};

// Re-export so existing consumers (e.g. use-auth-orchestration) don't break
export { resetAuthFailureCounter };

export default createBaseQuery;
